import { useState } from "react";
import {
  useGetLiquidationsStatusQuery,
  useGetLiquidationsClustersQuery,
  useGetLiquidationsForecastQuery,
} from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * LiquidationHeatmapCard — REAL liquidation density from the live
 * Binance futures forceOrder WebSocket. Aggregated into price buckets
 * around the current BTC mark.
 *
 * Two views:
 *   1. Top "above" buckets — historical short liquidations sitting
 *      above current price (squeeze targets if BTC rallies)
 *   2. Top "below" buckets — historical long liquidations below
 *      (cascade targets if BTC drops)
 *
 * Plus a forecast: "If BTC moves ±X%, ~$Y notional historically got
 * liquidated in that path."
 *
 * IMPORTANT: this is HISTORICAL density. It's a proxy for current
 * positioning (Binance doesn't expose per-leverage open positions
 * publicly). Areas with heavy historical liquidations tend to have
 * heavy positioning because market structure repeats.
 */
export function LiquidationHeatmapCard() {
  const [movePct, setMovePct] = useState<number>(5);
  const status = useGetLiquidationsStatusQuery(undefined, { pollingInterval: 30000 });
  const clusters = useGetLiquidationsClustersQuery(
    { lookback_hours: 24, bucket_pct: 0.5 },
    { pollingInterval: 60000 },
  );
  const forecastUp = useGetLiquidationsForecastQuery(
    { move_pct: movePct, lookback_hours: 168 },
    { pollingInterval: 120000 },
  );
  const forecastDown = useGetLiquidationsForecastQuery(
    { move_pct: -movePct, lookback_hours: 168 },
    { pollingInterval: 120000 },
  );

  const ws: any = status.data?.ws || {};
  const wsBybit: any = status.data?.ws_bybit || {};
  const sum: any = status.data?.summary_24h || {};
  const c: any = clusters.data || {};

  // Source mode precedence: Bybit (works from AWS) > external proxy >
  // Binance direct > waiting > blocked.
  const now = Date.now();
  function ageS(iso?: string) {
    if (!iso) return Infinity;
    return Math.max(0, (now - new Date(iso).getTime()) / 1000);
  }
  const bybitAge = ageS(wsBybit.last_event_at);
  const proxyAge = ageS(ws.last_ingest_at);
  const directAge = ageS(ws.last_event_at);
  const bybitFresh = bybitAge < 600 && (wsBybit.events_btc_total ?? 0) > 0;
  const proxyFresh = proxyAge < 300;
  const directFresh = directAge < 120 && (ws.events_btc_total ?? 0) > 0;

  let sourceMode: "bybit" | "proxy" | "direct" | "blocked" | "waiting" = "waiting";
  let sourceTone: "good" | "warn" | "bad" | "muted" = "muted";
  let sourceLabel = "connecting…";

  if (bybitFresh) {
    sourceMode = "bybit";
    sourceTone = "good";
    sourceLabel = bybitAge < 60
      ? `● Bybit live · ${Math.round(bybitAge)}s ago`
      : `● Bybit live · ${Math.round(bybitAge / 60)}m ago`;
  } else if (proxyFresh) {
    sourceMode = "proxy";
    sourceTone = "good";
    sourceLabel = proxyAge < 60
      ? `● proxy · last batch ${Math.round(proxyAge)}s ago`
      : `● proxy · last batch ${Math.round(proxyAge / 60)}m ago`;
  } else if (directFresh) {
    sourceMode = "direct";
    sourceTone = "good";
    sourceLabel = `● Binance direct · ${ws.events_btc_total} events`;
  } else if (wsBybit.thread_alive) {
    sourceMode = "waiting";
    sourceTone = "warn";
    sourceLabel = "● Bybit WS connected · waiting for first event";
  } else if (ws.stream_blocked_from_region) {
    sourceMode = "blocked";
    sourceTone = "bad";
    sourceLabel = "✕ region-blocked";
  } else if (ws.thread_alive) {
    sourceMode = "waiting";
    sourceTone = "warn";
    sourceLabel = "● WS connected · waiting for events";
  } else {
    sourceMode = "blocked";
    sourceTone = "bad";
    sourceLabel = "✕ disconnected";
  }
  // Stale-proxy fallback when proxy is the only path
  if (sourceMode !== "bybit" && sourceMode !== "proxy"
      && proxyAge < Infinity && proxyAge >= 300) {
    sourceMode = "blocked";
    sourceTone = "warn";
    sourceLabel = `⚠ proxy stale · last batch ${Math.round(proxyAge / 60)}m ago`;
  }

  const maxLiqInBucket = Math.max(
    ...((c.clusters_above || []).map((x: any) => x.total_liq_usd || 0)),
    ...((c.clusters_below || []).map((x: any) => x.total_liq_usd || 0)),
    1
  );

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-2 flex-wrap">
        <CardTitle className="flex items-center gap-2 flex-wrap">
          <span>BTC liquidation heatmap</span>
          <Badge tone={sourceTone}
                 title={sourceMode === "proxy"
                        ? `Receiving via external WS proxy (last ingest: ${ws.last_ingest_at || "—"}). Total ingested: ${ws.ingest_events_total ?? 0}.`
                        : sourceMode === "direct"
                          ? `Local WS streaming directly from Binance. Total events: ${ws.events_total ?? 0} (${ws.events_btc_total ?? 0} BTC).`
                          : sourceMode === "blocked"
                            ? "Binance futures market data not flowing to this host."
                            : "WS connected, waiting for events."}>
            {sourceLabel}
          </Badge>
          <span className="text-xs text-muted font-normal">
            real liquidations · density = squeeze magnets
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {clusters.isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : ws.stream_blocked_from_region && !ws.last_ingest_at && !bybitFresh ? (
          <div className="border-l-4 border-l-yellow-500 bg-bg2 rounded p-3 text-sm space-y-2">
            <div className="font-semibold text-yellow-500">
              ⚠ Binance futures WS restricted — needs external proxy
            </div>
            <div className="text-xs text-muted">
              The local connection completes and SUBSCRIBE is acknowledged,
              but no events flow. This is a known Binance regional policy
              on <code>fstream.binance.com</code>.
              <br /><br />
              <strong>Fix:</strong> deploy <code>proxy/liquidations_proxy.py</code>
              to any unrestricted VPS (~$5/month, EU/SG/JP). Set
              <code className="mx-1">LIQUIDATIONS_INGEST_TOKEN</code> on
              both sides. The proxy POSTs events to{" "}
              <code>/api/liquidations/ingest</code> in batches.
              See <code>proxy/README.md</code> for the 5-command deploy.
              <br /><br />
              The leverage-tier <strong>heuristic</strong> in the
              <strong> Contrarian sentiment</strong> card above gives
              sensible ±5% answers in the meantime.
            </div>
          </div>
        ) : !c.current_price ? (
          <div className="text-sm text-muted">
            Waiting for liquidation events. The subscriber needs a few
            hours of data to populate the density map. Events captured
            so far: <span className="font-mono">{ws.events_btc_total ?? 0}</span>
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            {/* Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
              <Stat label="BTC price" value={`$${Number(c.current_price).toLocaleString()}`} />
              <Stat label="Events 24h" value={String(c.n_events ?? 0)} />
              <Stat label="Longs liq 24h"
                     value={`$${(Number(sum.long_liq_notional || 0) / 1_000_000).toFixed(2)}M`}
                     tone="bad" />
              <Stat label="Shorts liq 24h"
                     value={`$${(Number(sum.short_liq_notional || 0) / 1_000_000).toFixed(2)}M`}
                     tone="good" />
            </div>

            {/* Heatmap — split above/below the current price */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Heatmap title="Shorts liquidated ABOVE (upside magnets)"
                        rows={(c.clusters_above || []).slice(0, 12)}
                        maxVal={maxLiqInBucket} />
              <Heatmap title="Longs liquidated BELOW (downside cascades)"
                        rows={(c.clusters_below || []).slice(0, 12)}
                        maxVal={maxLiqInBucket} />
            </div>

            {/* Forecast slider */}
            <div className="border border-border rounded p-3 bg-bg2">
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <span className="text-xs text-muted">If BTC moves ±</span>
                {[2, 3, 5, 8, 12].map(p => (
                  <button key={p}
                          onClick={() => setMovePct(p)}
                          className={`text-xs px-2 py-1 rounded border ${
                            movePct === p ? "bg-accent text-white border-accent"
                                          : "border-border text-muted hover:text-fg"
                          }`}>
                    {p}%
                  </button>
                ))}
                <span className="text-xs text-muted ml-2">
                  historical density over last 7 days
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                <ForecastBox title={`UP +${movePct}%`}
                              forecast={forecastUp.data}
                              positiveSide="shorts" />
                <ForecastBox title={`DOWN -${movePct}%`}
                              forecast={forecastDown.data}
                              positiveSide="longs" />
              </div>
            </div>

            {/* Source line */}
            <div className="text-xs text-muted flex items-center gap-2 flex-wrap pt-1 border-t border-border/40">
              <span className="text-muted">data path:</span>
              {sourceMode === "bybit" ? (
                <>
                  <Badge tone="good">Bybit WS (direct)</Badge>
                  <span>last event {bybitAge < 60 ? `${Math.round(bybitAge)}s` : `${Math.round(bybitAge / 60)}m`} ago · {wsBybit.events_btc_total ?? 0} events</span>
                </>
              ) : sourceMode === "proxy" ? (
                <>
                  <Badge tone="good">via external proxy</Badge>
                  <span>last batch {proxyAge < 60 ? `${Math.round(proxyAge)}s` : `${Math.round(proxyAge / 60)}m`} ago · {ws.ingest_events_total ?? 0} total ingested</span>
                </>
              ) : sourceMode === "direct" ? (
                <>
                  <Badge tone="good">Binance direct</Badge>
                  <span>last event {Math.round(directAge)}s ago</span>
                </>
              ) : (
                <Badge tone="warn">no live source</Badge>
              )}
            </div>

            {/* Disclosure */}
            <div className="text-xs text-muted">
              <Badge tone="muted">note</Badge>{" "}
              Binance doesn't publish per-leverage open positions. This
              map is the EMPIRICAL DENSITY of forced liquidations —
              concentrations historically signal where current
              positioning clusters too (market structure repeats), but
              they are not a direct read of current OI per leverage tier.
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: "good" | "bad" | "muted" }) {
  return (
    <div className="border border-border rounded p-2 bg-bg2">
      <div className="text-xs text-muted">{label}</div>
      <div className={`font-mono font-semibold ${
        tone === "good" ? "text-emerald-400"
        : tone === "bad" ? "text-rose-400"
        : ""
      }`}>{value}</div>
    </div>
  );
}

function Heatmap({ title, rows, maxVal }: {
  title: string;
  rows: any[];
  maxVal: number;
}) {
  if (!rows || rows.length === 0) {
    return (
      <div className="border border-border rounded p-2">
        <div className="text-xs text-muted mb-1">{title}</div>
        <div className="text-xs text-muted italic">No data yet — waiting for liquidation events.</div>
      </div>
    );
  }
  // For the "above" view we want lowest prices at top (closest to current)
  // For the "below" view we want highest prices at top (closest to current)
  // The backend already returns them sorted by distance from current ASC,
  // so just trust the order.
  return (
    <div className="border border-border rounded p-2">
      <div className="text-xs text-muted mb-1">{title}</div>
      <table className="w-full text-xs">
        <thead className="text-muted">
          <tr>
            <th className="text-left py-0.5">Price band</th>
            <th className="text-right">Δ%</th>
            <th className="text-right">Long $</th>
            <th className="text-right">Short $</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r: any, i: number) => {
            const dist = Number(r.distance_pct || 0);
            void maxVal;   // reserved for future bar-width rendering
            return (
              <tr key={i} className="border-t border-border/30">
                <td className="py-0.5 font-mono">
                  ${Number(r.low).toFixed(0)}–${Number(r.high).toFixed(0)}
                </td>
                <td className={`text-right font-mono ${
                  dist > 0 ? "text-emerald-400" : "text-rose-400"
                }`}>
                  {dist > 0 ? "+" : ""}{dist.toFixed(2)}%
                </td>
                <td className="text-right font-mono text-rose-400">
                  {(Number(r.long_liq_usd) / 1000).toFixed(0)}K
                </td>
                <td className="text-right font-mono text-emerald-400">
                  {(Number(r.short_liq_usd) / 1000).toFixed(0)}K
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ForecastBox({ title, forecast, positiveSide }: {
  title: string;
  forecast: any;
  positiveSide: "longs" | "shorts";
}) {
  if (!forecast || forecast.error) {
    return (
      <div className="border border-border rounded p-2">
        <div className="text-xs text-muted">{title}</div>
        <div className="text-xs text-muted italic">no forecast (need more historical density)</div>
      </div>
    );
  }
  const longs = Number(forecast.longs_liq_in_path_usd || 0);
  const shorts = Number(forecast.shorts_liq_in_path_usd || 0);
  const upside = positiveSide === "shorts";
  return (
    <div className="border border-border rounded p-2 bg-bg">
      <div className="text-xs text-muted mb-1">{title} · to ${Number(forecast.target_price).toLocaleString()}</div>
      <div className={`text-lg font-bold tabular-nums ${
        upside ? "text-emerald-400" : "text-rose-400"
      }`}>
        ${((upside ? shorts : longs) / 1_000_000).toFixed(2)}M
      </div>
      <div className="text-xs text-muted">
        {upside ? "shorts squeezed" : "longs cascaded"}
        {" · "}
        opposite side: ${((upside ? longs : shorts) / 1_000_000).toFixed(2)}M
      </div>
    </div>
  );
}
